import { useEffect, useState, useMemo } from 'react';
import { UserDetails } from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import { TaskCenterQueries } from '@frontend/api-task-center';
import { UsersQueries } from '@frontend/api-users';
import { getUser, isWeaveUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { useTaskCenterStore } from '@frontend/task-center-scope';
import { useAlert } from '@frontend/design-system';

const EMPTY_ARRAY: UserDetails[] = [];

export const useAssigneeByLocation = (locationIds: string[], phoneNumber?: string) => {
  const { t } = useTranslation('task-center');
  const alert = useAlert();
  const { shouldFilterPrivateComms } = useTaskCenterStore(['shouldFilterPrivateComms']);
  const [authorizedUsers, setAuthorizedUsers] = useState<UserDetails[]>([]);

  const loggedInUser = getUser();
  const isLoggedInUserWeaveUser = isWeaveUser();

  const {
    data,
    isLoading: isLoadingPrivateUsers,
    isFetched: isPrivateCommsFetched,
    isError,
  } = TaskCenterQueries.useGetPhoneAuthorizedAssignees(
    {
      e164PhoneNumbers: [phoneNumber ?? ''],
    },
    {
      enabled: Boolean(phoneNumber) && shouldFilterPrivateComms,
      onError: () =>
        alert.error(
          t(
            `We're having trouble loading assignees. You can still create your task and assign it later. If this keeps happening, please contact support.`
          )
        ),
    }
  );

  const regularAssigneesQuery = UsersQueries.useGetUsersInLocations(locationIds);

  const fallbackUser = useMemo(() => {
    // If the logged in user is not a Weave user, we return undefined
    if (isLoggedInUserWeaveUser) return;
    // Find and return the logged in user from the regular assignees list
    const selfUser = regularAssigneesQuery.data?.users?.find((user) => user.userId === loggedInUser?.userID);
    return selfUser;
  }, [loggedInUser, isLoggedInUserWeaveUser, regularAssigneesQuery.data?.users]);

  useEffect(() => {
    // We do not want to filter the users if:
    // 1. The feature flag is off
    if (!shouldFilterPrivateComms) return;
    // 2. The assignees query is not fetched
    if (!regularAssigneesQuery.isFetched) return;
    // 3. If feature flag is on but private comms query is not fetched
    if (shouldFilterPrivateComms && isPrivateCommsFetched) return;
    // 4. If there are no users in the assignees query
    if (!regularAssigneesQuery.data?.users?.length) return;
    // 5. If there are no users in the phone number authorized users query
    if (!data?.phoneNumberAuthorizedUsers?.length) return;
    // 6. If all users are authorized
    if (data?.phoneNumberAuthorizedUsers?.[0]?.allUsersAuthorized) return;

    const accessibleUsers = regularAssigneesQuery.data.users.filter(
      (user) => user.userId && data.phoneNumberAuthorizedUsers?.[0]?.userIds?.includes(user.userId)
    );
    // If the accessible users is undefined, we set it to self user and lastly an empty array if weave user is logged in
    setAuthorizedUsers(accessibleUsers ?? fallbackUser ?? EMPTY_ARRAY);
  }, [
    data?.phoneNumberAuthorizedUsers,
    shouldFilterPrivateComms,
    regularAssigneesQuery.data,
    regularAssigneesQuery.isFetched,
    isPrivateCommsFetched,
    fallbackUser,
  ]);

  return {
    ...regularAssigneesQuery,
    // If there is an error in fetching private comms assignees, then we do not know who is authorized hence
    // we return an the user who is creating the task as the only assignee
    ...(isError && {
      data: {
        users: fallbackUser ? [fallbackUser] : EMPTY_ARRAY,
      },
    }),
    ...(shouldFilterPrivateComms &&
      data?.phoneNumberAuthorizedUsers?.[0]?.allUsersAuthorized === false && {
        isLoading: regularAssigneesQuery.isLoading || isLoadingPrivateUsers,
        data: {
          ...regularAssigneesQuery.data,
          users: authorizedUsers,
        },
      }),
  };
};
